import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, message, Button, Form, Tag, theme, Flex } from 'antd';
import { SafetyCertificateOutlined, SaveOutlined } from '@ant-design/icons';
import { useErrorHandler } from '../../../../Hooks/useErrorHandler';
import { updateChannel } from '../../ChannelsServices';
import { resolveBackendMessage } from '../../../../Utils/apiMessages';
import ChannelForm from '../../ChannelForm/ChannelForm';

const { Title } = Typography;

/**
 * SettingsTab - Channel settings and edit form
 * Features:
 * - Update channel properties
 * - Change associated agent
 * - Update synchronization settings
 * - Update credential (if needed)
 */
const SettingsTab = ({ channel, onUpdate }) => {
    const { t } = useTranslation();
    const { handleErrorForm } = useErrorHandler();
    const { token } = theme.useToken();
    const [form] = Form.useForm();
    const [updating, setUpdating] = useState(false);

    const handleUpdate = async () => {
        try {
            const values = await form.validateFields();
            setUpdating(true);

            // Filter to only allowed update fields
            const payload = {};
            if (values.agent_id !== undefined) payload.agent_id = values.agent_id;
            if (values.is_synchronization_enabled !== undefined) payload.is_synchronization_enabled = values.is_synchronization_enabled;
            // Name and description are disabled in edit mode and should not be sent
            if (values.credential_id !== undefined) payload.credential_id = values.credential_id;

            const response = await updateChannel(channel.id, payload);
            const text = resolveBackendMessage(t, response);
            if (text) message.success(text);

            // Refresh parent channel data
            if (onUpdate) onUpdate();
        } catch (error) {
            handleErrorForm(error, form);
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            <ChannelForm
                form={form}
                initialValues={{
                    ...channel,
                    agent_id: channel.agent?.id || null,
                }}
                isEdit={true}
            />

            {channel.permissions_json?.scopes && channel.permissions_json.scopes.length > 0 && (
                <div style={{
                    background: token.colorFillAlter,
                    padding: '16px',
                    borderRadius: token.borderRadius,
                    border: `1px solid ${token.colorBorderSecondary}`
                }}>
                    <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
                        <SafetyCertificateOutlined style={{ color: token.colorSuccess }} />
                        <Typography.Title level={5} style={{ margin: 0 }}>
                            {t('granted_permissions')}
                        </Typography.Title>
                    </Flex>

                    <Flex wrap="wrap" gap={8}>
                        {channel.permissions_json.scopes.map(scope => (
                            <Tag
                                key={scope}
                                color="blue"
                                style={{ margin: 0 }}
                            >
                                {scope.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                            </Tag>
                        ))}
                    </Flex>
                </div>
            )}

            <Flex
                gap="middle"
                wrap="wrap"
                align="center"
                justify="flex-end"
                style={{
                    width: '100%',
                    padding: '8px 0 4px'
                }}
            >
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleUpdate}
                    loading={updating}
                >
                    {t('save_changes')}
                </Button>
            </Flex>
        </div>
    );
};

export default SettingsTab;
